import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants, EventId } from '@ps/isp-web-services-types';
import type { VolleyballFixture, VolleyballSet } from '@ps/web-shared-generated-graphql/sports';
import { isEmpty } from 'lodash';
import React from 'react';

import { EventTypeId } from '@swf/sports/types/eventType';
import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';
import { getVolleyballMatchScore } from '@swf/sports/utils/getVolleyballMatchScore';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventId: EventId;
  eventTime: string;
  volleyballFixture?: VolleyballFixture;
}

const isVolleyballSet = (volleyballSet: VolleyballSet | null): volleyballSet is VolleyballSet =>
  !isEmpty(volleyballSet);

const getPreviousSets = (previousSets: VolleyballFixture['previousSets']): VolleyballSet[] =>
  previousSets?.filter(isVolleyballSet) ?? [];

const getParticipantScore = (
  participant: EnrichedParticipants[number],
  score: VolleyballSet['score'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const Volleyball: React.FC<Props> = ({
  enrichedParticipants,
  volleyballFixture,
  eventId,
  eventTime
}) => {
  const [participantA, participantB] = enrichedParticipants ?? [];
  const currentSet = volleyballFixture?.currentSet;
  const currentSetScore = currentSet?.score;
  const previousSets = volleyballFixture?.previousSets;
  const currentServingParticipant = currentSet?.currentServer;
  const setsWon = getVolleyballMatchScore(previousSets);

  const hasPreviousSets = !isEmpty(previousSets);
  const sets = getPreviousSets(previousSets);
  return (
    <ScoreboardWrapper eventId={eventId} eventTime={eventTime} eventTypeId={EventTypeId.VOLLEYBALL}>
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant
            name={participantA.participant}
            isVisibleMarker={currentServingParticipant === participantA.type}
          />
          <ScoreboardParticipant
            name={participantB.participant}
            isVisibleMarker={currentServingParticipant === participantB.type}
          />
        </ScoreboardParticipants>
        <ScoreboardScores>
          <ScoreboardScore
            isHighlighted
            score={[
              getParticipantScore(participantA, setsWon) ?? null,
              getParticipantScore(participantB, setsWon) ?? null
            ]}
            scoreCharacterLength={1}
          />
          {hasPreviousSets
            ? sets.map((set) => (
                <ScoreboardScore
                  key={set.number}
                  score={[
                    getParticipantScore(participantA, set.score) ?? null,
                    getParticipantScore(participantB, set.score) ?? null
                  ]}
                  scoreCharacterLength={getScoreCharacterLength(EventTypeId.VOLLEYBALL, [
                    set.score.home,
                    set.score.away
                  ])}
                />
              ))
            : null}
          <ScoreboardScore
            score={[
              getParticipantScore(participantA, currentSetScore) ?? null,
              getParticipantScore(participantB, currentSetScore) ?? null
            ]}
            scoreCharacterLength={getScoreCharacterLength(EventTypeId.VOLLEYBALL, [
              currentSetScore?.home ?? 0,
              currentSetScore?.away ?? 0
            ])}
          />
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default Volleyball;
